import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DollarSign,
  FileText,
  BarChart3,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Building2,
} from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import {
  BillingStatsDTO,
  serviceTypeLabels,
  ServiceType,
  formatMoney,
} from '../../types/billing';
import { SkeletonStatsCard } from '../../components/Common/Skeleton';
import { istMonth } from './closeBooksRules';
import { useLatestRequest } from './latestRequest';

/** How often the open dashboard re-reads the figures; the charger posts about once a minute. */
const REFRESH_MS = 60_000;

/** `2026-10` → `October 2026`. */
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const VoiceStatementDownload: React.FC<{ month: string }> = ({ month }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    const result = await billingService.downloadVoiceStatement(month);
    if (!result.ok) setError(result.error);
    setBusy(false);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-medium text-gray-900 dark:text-white">Monthly voice statement</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Per provider and per business: billed minutes, base, 18% GST and final (.xlsx).
        </p>
      </div>
      <span className="text-sm text-gray-600 dark:text-gray-300">{monthLabel(month)}</span>
      <button
        onClick={download}
        disabled={busy || !month}
        className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
      >
        <FileText className="h-4 w-4 mr-2" />
        {busy ? 'Preparing…' : 'Download'}
      </button>
      {error && <p className="w-full text-sm text-red-600">{error}</p>}
    </div>
  );
};

const BillingDashboard: React.FC = () => {
  const [stats, setStats] = useState<BillingStatsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** One month drives every figure on the page and the statement download. IST, like billing. */
  const [month, setMonth] = useState(istMonth());
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  /** Following the current month: when the IST month turns over, move with it. */
  const followCurrent = useRef(true);
  const isCurrent = month === istMonth();
  const beginRequest = useLatestRequest();

  const loadStats = useCallback(async (m: string, quiet = false) => {
    // Picking September while August is still loading must not end on August's figures.
    const isLatest = beginRequest();
    if (!quiet) setLoading(true);
    const response = await billingService.getStats(m);
    if (!isLatest()) return;
    if (response.success && response.data) {
      setStats(response.data);
      setError(null);
      setUpdatedAt(new Date());
    } else if (!quiet) {
      setError(response.error || 'Failed to load billing statistics');
    }
    // Always: a background refresh may have superseded the request that showed the skeleton.
    setLoading(false);
  }, [beginRequest]);

  useEffect(() => {
    loadStats(month);
  }, [month, loadStats]);

  // Keep the open page current: re-read every minute while it is visible and whenever it
  // becomes visible again; and when the IST day or month turns over, "today" and "this
  // month" move on with it instead of showing yesterday's figures until someone reloads.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const now = istMonth();
      if (followCurrent.current && now !== month) {
        setMonth(now);
        return;
      }
      loadStats(month, true);
    };
    const id = window.setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [month, loadStats]);

  const chooseMonth = (m: string) => {
    if (!m) return;
    followCurrent.current = m === istMonth();
    setMonth(m);
  };

  /** Totals across accounts; every live account is INR. */
  const formatCurrency = (value?: number) => formatMoney(value ?? 0, 'INR');

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing Dashboard</h1>
        <div className="flex flex-wrap items-center gap-3">
          {updatedAt && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Updated {updatedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST
            </span>
          )}
          <input
            id="billing-month"
            type="month"
            aria-label="Month"
            value={month}
            max={istMonth()}
            onChange={(e) => chooseMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
          />
          <button
            onClick={() => loadStats(month)}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            Refresh
          </button>
        </div>
      </div>

      <VoiceStatementDownload month={month} />

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Credits Balance</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats?.totalCreditsBalance)}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Credits Used Today (IST)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {isCurrent && stats?.totalCreditsUsedToday != null ? formatCurrency(stats.totalCreditsUsedToday) : '—'}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
              <TrendingDown className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{isCurrent ? 'Credits Used This Month' : `Credits Used in ${monthLabel(month)}`}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats?.totalCreditsUsedThisMonth)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{isCurrent ? 'Total Revenue (Month)' : `Revenue, ${monthLabel(month)}`}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats?.totalRevenue)}
              </p>
            </div>
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Account Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Accounts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(stats?.totalAccounts)}
              </p>
            </div>
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Building2 className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <span className="text-green-600">{stats?.prepaidAccounts} Prepaid</span>
            <span className="text-blue-600">{stats?.postpaidAccounts} Postpaid</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Low Balance Accounts</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {formatNumber(stats?.lowBalanceAccounts)}
              </p>
            </div>
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Invoices</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(stats?.pendingInvoices)}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <FileText className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Overdue Invoices</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatNumber(stats?.overdueInvoices)}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Outstanding: {formatCurrency(stats?.totalOutstanding)}
            </p>
          </div>
        </div>
      </div>

      {/* Usage by Service Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage by Service Type · {monthLabel(month)}</h2>
          {stats?.usageByServiceType && Object.keys(stats.usageByServiceType).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.usageByServiceType)
                .sort((a, b) => b[1] - a[1])
                .map(([serviceType, credits]) => {
                  const maxCredits = Math.max(...Object.values(stats.usageByServiceType || {}));
                  const percentage = maxCredits > 0 ? (credits / maxCredits) * 100 : 0;
                  return (
                    <div key={serviceType}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {serviceTypeLabels[serviceType as ServiceType] || serviceType}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(credits)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No usage data available</p>
          )}
        </div>

        {/* Top Consumers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Consumers · {monthLabel(month)}</h2>
          {stats?.topConsumers && stats.topConsumers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase py-2">
                      Organization
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase py-2">
                      Credits Used
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase py-2">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {stats.topConsumers.map((consumer, index) => (
                    <tr key={consumer.organizationId}>
                      <td className="py-3">
                        <div className="flex items-center">
                          <span className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-full flex items-center justify-center text-xs font-medium mr-2">
                            {index + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {consumer.organizationName || consumer.organizationId}
                          </span>
                        </div>
                      </td>
                      <td className="text-right py-3 text-sm text-gray-900 dark:text-white">
                        {formatCurrency(consumer.creditsUsed)}
                      </td>
                      <td className="text-right py-3 text-sm">
                        <span
                          className={
                            (consumer.currentBalance || 0) < 100
                              ? 'text-red-600'
                              : 'text-green-600'
                          }
                        >
                          {formatCurrency(consumer.currentBalance)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No consumer data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingDashboard;
